import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";

type Row = {
  run_id: number;
  name: string;
  bench_name: string;
  started_at: number;
  passed: number;
  failed: number;
  skipped: number;
  xfailed: number;
  steps: number;
};

/**
 * Segment colours: the Teleste palette at 100%, which is what the brand book
 * permits for charts (100% and 50% only, no other steps).
 *
 * They are also the accessible choice here, which is worth stating because the
 * colours they REPLACE claimed to be. The previous set - dark green for passed
 * beside dark slate for skipped - carried a comment promising a worst adjacent
 * pair of 14.2 (OKLab dE x100, deuteranopia at full severity) against a target
 * of 8. Re-measured on 2026-08-24 it is 6.6, i.e. under the target it cites:
 * deuteranopia collapses green toward grey, so those two segments sit almost on
 * top of each other for the readers the check existed to protect. The number
 * appears to have been asserted rather than computed, and the same sentence is
 * in tools/hil/report_charts.py.
 *
 * The light set measures 19.4 and the dark one 19.1. src/palette.test.ts
 * computes both, so the claim in this comment is checked by CI rather than
 * trusted - which is the part the old one got wrong. The values live in
 * index.css as custom properties because an SVG fill= attribute cannot take
 * a Tailwind dark: variant.
 */
export const STACK = [
  ["failed", "var(--chart-failed)"],
  ["xfailed", "var(--chart-xfailed)"],
  ["skipped", "var(--chart-skipped)"],
  ["passed", "var(--chart-passed)"],
] as const;

/** What those variables resolve to, for the tests that measure them. */
export const CHART_LIGHT = ["#de473c", "#f5cd23", "#c3c3c8", "#3daf2c"];
export const CHART_DARK = ["#de473c", "#f5cd23", "#e1e1e4", "#3daf2c"];

const TRACK = "var(--chart-track)";
const GRID = "var(--chart-grid)";
const AXIS = "var(--chart-axis)";
const INK = "var(--chart-ink)";
const SEG_GAP = 2;

/**
 * The top of the scale, robust to one outlier (step K6).
 *
 * On the Runs page one run of 280 cases (run115) set the scale for thirty
 * runs of 5 to 50, and every other column was a sliver. When the largest run
 * is more than 2.5 times the 90th percentile, the scale stops at 1.5 times
 * that percentile and the taller columns are cut, each labelled with its
 * real total - so the outlier is still said, and the rest can be read.
 */
export function robustTop(totals: number[]): { top: number; clipped: boolean } {
  if (!totals.length) return { top: 0, clipped: false };
  const sorted = [...totals].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1];
  const p90 = sorted[Math.min(sorted.length - 1, Math.floor(0.9 * (sorted.length - 1)))];
  if (sorted.length >= 5 && p90 > 0 && max > 2.5 * p90) return { top: Math.ceil(1.5 * p90), clipped: true };
  return { top: max, clipped: false };
}

/** A rounded ceiling and whole-number ticks - never 0..37 in steps of 9.25. */
export function niceAxis(top: number, ticks = 4): [number, number[]] {
  if (top <= 0) return [ticks, Array.from({ length: ticks + 1 }, (_, i) => i)];
  const raw = top / ticks;
  let step = 1;
  while (step * 10 < raw) step *= 10;
  for (const m of [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10]) {
    if (step * m >= raw) {
      step = Math.trunc(step * m) || 1;
      break;
    }
  }
  let hi = step * ticks;
  while (hi < top) hi += step;
  const out: number[] = [];
  for (let v = 0; v <= hi; v += step) out.push(v);
  return [hi, out];
}

/** A column path: rounded at the data end, square on the baseline. */
export function col(x: number, y: number, w: number, h: number, r = 0): string {
  if (h <= 0 || w <= 0) return "";
  const rr = Math.min(r, w / 2, h);
  if (rr <= 0.6) return `M${x},${y}h${w}v${h}h${-w}z`;
  return (
    `M${x},${y + h}V${y + rr}` +
    `a${rr},${rr} 0 0 1 ${rr},${-rr}` +
    `h${w - 2 * rr}a${rr},${rr} 0 0 1 ${rr},${rr}V${y + h}z`
  );
}

/**
 * How the suite has moved across runs.
 *
 * ONE SHARED AXIS, not a bar-per-run scaled to itself. An earlier version drew
 * each run to its own height so a filtered three-case run would not look like a
 * collapse - but that hid the thing the chart is for. A run of three cases
 * BEING small is information, and a shared axis with a track behind each column
 * says both what a run contained and how much of the suite it covered.
 */
/** The key goes to the API; the label is a catalogue key resolved at render. */
const SCOPES = [
  { key: "", label: "history.scopeAll" },
  { key: "full", label: "history.scopeFull" },
  { key: "partial", label: "history.scopePartial" },
] as const;

export default function History({ benchId }: { benchId?: number }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>([]);
  const [scope, setScope] = useState("");
  // THE RELIEF THE CONTRAST CHECK OBLIGES. The four verdict colours were
  // measured for colour-vision separation and pass; nobody had measured them
  // against the SURFACE, and three of the four are under 3:1 - yellow 1.50,
  // grey 1.71, green 2.78. Segments that cannot be read are not an encoding.
  // The numbers are in `<title>` on each segment, which is a mouse-only,
  // hover-only answer; this is the same data as a table, for touch, for a
  // screen reader, and for anyone who wants to read rather than estimate.
  const [asTable, setTable] = useState(false);
  const [avail, setAvail] = useState<Record<string, number>>({});
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    api
      .history(benchId, scope)
      .then((d) => {
        if (!alive) return;
        setRows(d.history);
        setAvail(d.available ?? {});
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [benchId, scope]);

  const hist = rows.filter(
    (r) => r.passed + r.failed + r.skipped + r.xfailed > 0,
  );

  // The picker renders even when the chart cannot, because the usual reason a
  // filtered chart is empty is the filter - and a component that returns null
  // takes the only control that would undo it with it.
  const picker = (
    <div className="mb-2 flex flex-wrap items-center gap-1 text-xs">
      {SCOPES.map((sc) => {
        const n = sc.key ? (avail[sc.key] ?? 0)
                         : Object.values(avail).reduce((a, b) => a + b, 0);
        return (
          <button
            key={sc.key}
            onClick={() => setScope(sc.key)}
            className={`rounded px-2 py-1 ${
              scope === sc.key
                ? "bg-brand-700 text-white"
                : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            {t(sc.label)}
            <span className="ml-1 tabular-nums opacity-70">{n}</span>
          </button>
        );
      })}
      <button
        onClick={() => setTable(!asTable)}
        aria-pressed={asTable}
        className="rounded px-2 py-1 text-slate-500 underline hover:bg-slate-100 dark:hover:bg-slate-800"
      >
        {asTable ? t("history.chart") : t("history.table")}
      </button>
      {(avail.unknown ?? 0) > 0 && (
        <span
          // Said out loud rather than folded into "all": these are runs from
          // before the selection was recorded, and quietly counting them as
          // whole suites is the one thing this filter must not do.
          title={t("history.unknownTitle")}
          className="ml-1 text-slate-400"
        >
          {t("history.unknownCount", { count: avail.unknown })}
        </span>
      )}
    </div>
  );

  if (hist.length < 2) {
    if (!scope) return null;         // nothing to show and nothing chosen
    return (
      <section>
        {picker}
        <p className="text-sm text-slate-500">
          {/* Two whole sentences, not one with a word swapped in: "whole-suite
              runs" and "partial runs" decline differently in Polish, and the
              adjective has to agree with the noun it is next to. */}
          {scope === "full" ? t("history.tooFewFull") : t("history.tooFewPartial")}
        </p>
      </section>
    );
  }

  const VW = 900;
  const PL = 34;
  const PR = 8;
  const H = 170;
  const TOP = 10;
  const base = TOP + H;
  const totals = hist.map((r) => r.passed + r.failed + r.skipped + r.xfailed);
  const robust = robustTop(totals);
  const [top, ticks] = niceAxis(robust.top);
  const sc = H / top;
  const bw = (VW - PL - PR) / hist.length;
  const w = Math.min(26, bw * 0.62);
  const every = Math.ceil(hist.length / 12);

  return (
    <section className="mt-6">
      <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("history.heading")}
      </h2>
      <div className="mt-2">{picker}</div>
      {asTable ? (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs font-medium text-slate-500 dark:bg-slate-800">
              <tr>
                <th scope="col" className="px-3 py-2">{t("history.colRun")}</th>
                <th scope="col" className="px-3 py-2 text-right">{t("history.colFailed")}</th>
                <th scope="col" className="px-3 py-2 text-right">{t("history.colXfailed")}</th>
                <th scope="col" className="px-3 py-2 text-right">{t("history.colSkipped")}</th>
                <th scope="col" className="px-3 py-2 text-right">{t("history.colPassed")}</th>
                <th scope="col" className="px-3 py-2 text-right">{t("history.colCases")}</th>
              </tr>
            </thead>
            <tbody>
              {[...hist].reverse().map((r) => (
                <tr key={r.run_id}
                    className="border-t border-slate-200 dark:border-slate-800">
                  <td className="px-3 py-1.5 font-medium">
                    <Link className="hover:underline" to={`/runs/${r.run_id}`}>{r.name}</Link>
                  </td>
                  <td className={`px-3 py-1.5 text-right tabular-nums ${
                    r.failed ? "font-semibold text-red-600 dark:text-red-400" : "text-slate-400"
                  }`}>{r.failed}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{r.xfailed}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{r.skipped}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{r.passed}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {r.passed + r.failed + r.skipped + r.xfailed}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
        <svg
          viewBox={`0 0 ${VW} ${base + 34}`}
          className="w-full"
          role="img"
          aria-label={t("history.chartLabel")}
        >
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={PL}
                y1={base - v * sc}
                x2={VW - PR}
                y2={base - v * sc}
                stroke={GRID}
              />
              <text
                x={PL - 6}
                y={base - v * sc + 3.5}
                textAnchor="end"
                fontSize={10}
                fill={INK}
              >
                {v}
              </text>
            </g>
          ))}

          {hist.map((r, i) => {
            const x = PL + i * bw + (bw - w) / 2;
            const total = totals[i];
            let acc = 0;
            return (
              <g
                key={r.run_id}
                className="cursor-pointer"
                onClick={() => nav(`/runs/${r.run_id}`)}
              >
                {/* The track is the run's own total: a column can never be
                    taller, so a short one is a small run rather than a bad one. */}
                {/* Cut at the top of a robust scale, and said: the real total
                    above the column, so an outlier is shown, not hidden. */}
                {total > top && (
                  <text x={x + w / 2} y={TOP - 1} textAnchor="middle" fontSize={10} fill={INK}>
                    ▲{total}
                  </text>
                )}
                <path d={col(x, base - Math.min(total, top) * sc, w, Math.min(total, top) * sc, 4)} fill={TRACK}>
                  <title>
                    {t("history.trackTitle", { name: r.name, count: total })}
                  </title>
                </path>
                {STACK.map(([k, colour], j) => {
                  const n = r[k as keyof Row] as number;
                  if (!n) return null;
                  // Scaled into the cut column: the segments keep their shares.
                  const hp = (n / total) * Math.min(total, top) * sc;
                  const y = base - acc - hp;
                  acc += hp;
                  const drawn = Math.max(hp - SEG_GAP, 1);
                  return (
                    <path
                      key={k}
                      d={col(x, y, w, drawn, j === STACK.length - 1 ? 4 : 0)}
                      fill={colour}
                    >
                      <title>
                        {t("history.segTitle", {
                          name: r.name,
                          count: n,
                          outcome: t(`verdict.legend.${k}` as "verdict.legend.passed"),
                          pct: Math.round((100 * n) / total),
                          total,
                        })}
                      </title>
                    </path>
                  );
                })}
                {i % every === 0 && (
                  <text
                    x={x + w / 2}
                    y={base + 14}
                    textAnchor="middle"
                    fontSize={10}
                    fill={INK}
                  >
                    {r.name.replace(/^run/, "")}
                  </text>
                )}
              </g>
            );
          })}

          <line x1={PL} y1={base} x2={VW - PR} y2={base} stroke={AXIS} />
          <text x={PL} y={base + 29} fontSize={10} fill={INK}>
            {t("history.axisRun")}
          </text>
        </svg>

        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-slate-500">
          {STACK.map(([k, colour]) => (
            <span key={k} className="flex items-center gap-1">
              <span
                className="inline-block h-2 w-2 rounded-sm"
                style={{ background: colour }}
              />
              {t(`verdict.legend.${k}` as "verdict.legend.passed")}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: TRACK }}
            />
            {t("history.legendTrack")}
          </span>
          <span className="ml-auto">{t("history.legendNote")}</span>
        </div>
      </div>
      )}
    </section>
  );
}
